import { useState, useEffect } from "react";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Checkbox } from "../../components/ui/checkbox";
import { Switch } from "../../components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import { AdminRecordModal, AdminModalFooter } from "../../components/admin/AdminRecordModal";
import { toast } from "sonner";
import {
  Plus,
  Package,
  PencilSimple,
  Trash,
  CheckCircle,
  XCircle,
  ArrowsClockwise,
  Tag,
} from "phosphor-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function AdminBundles() {
  const [bundles, setBundles] = useState([]);
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingBundle, setEditingBundle] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    slug: "",
    description: "",
    price_monthly: 0,
    price_yearly: 0,
    feature_ids: [],
    discount_percent: 0,
    is_active: true,
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [bundlesRes, featuresRes] = await Promise.all([
        fetch(`${API}/admin/bundles`, { credentials: "include" }),
        fetch(`${API}/admin/features`, { credentials: "include" }),
      ]);
      if (bundlesRes.ok) setBundles((await bundlesRes.json()).bundles);
      if (featuresRes.ok) setFeatures((await featuresRes.json()).features);
    } catch (error) {
      console.error("Error:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const url = editingBundle
        ? `${API}/admin/bundles/${editingBundle.bundle_id}`
        : `${API}/admin/bundles`;
      const method = editingBundle ? "PUT" : "POST";
      const response = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(formData),
      });
      if (response.ok) {
        toast.success(editingBundle ? "Bundle updated" : "Bundle created");
        setIsDialogOpen(false);
        resetForm();
        fetchData();
      } else {
        const err = await response.json();
        toast.error(err.detail || "Failed to save bundle");
      }
    } catch (error) {
      toast.error("Failed to save bundle");
    }
  };

  const toggleActive = async (bundle) => {
    try {
      const response = await fetch(`${API}/admin/bundles/${bundle.bundle_id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ is_active: !bundle.is_active }),
      });
      if (response.ok) {
        toast.success(bundle.is_active ? "Bundle deactivated" : "Bundle activated");
        fetchData();
      } else {
        toast.error("Failed to update bundle status");
      }
    } catch (error) {
      toast.error("Failed to update bundle status");
    }
  };

  const deleteBundle = async (bundleId) => {
    if (!window.confirm("Delete this bundle? This cannot be undone.")) return;
    try {
      await fetch(`${API}/admin/bundles/${bundleId}`, {
        method: "DELETE",
        credentials: "include",
      });
      toast.success("Bundle deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete bundle");
    }
  };

  const editBundle = (bundle) => {
    setEditingBundle(bundle);
    setFormData({
      name: bundle.name || "",
      slug: bundle.slug || "",
      description: bundle.description || "",
      price_monthly: bundle.price_monthly || 0,
      price_yearly: bundle.price_yearly || 0,
      feature_ids: bundle.feature_ids || [],
      discount_percent: bundle.discount_percent || 0,
      is_active: bundle.is_active !== false,
    });
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setEditingBundle(null);
    setFormData({
      name: "",
      slug: "",
      description: "",
      price_monthly: 0,
      price_yearly: 0,
      feature_ids: [],
      discount_percent: 0,
      is_active: true,
    });
  };

  const toggleFeature = (featureId) => {
    setFormData((prev) => ({
      ...prev,
      feature_ids: prev.feature_ids.includes(featureId)
        ? prev.feature_ids.filter((id) => id !== featureId)
        : [...prev.feature_ids, featureId],
    }));
  };

  const generateSlug = (name) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_|_$/g, "");
  };

  // Calculate total value of selected features
  const selectedFeatures = features.filter((f) =>
    formData.feature_ids.includes(f.feature_id)
  );
  const totalMonthlyValue = selectedFeatures.reduce(
    (sum, f) => sum + (f.price_monthly || 0),
    0
  );
  const totalYearlyValue = selectedFeatures.reduce(
    (sum, f) => sum + (f.price_yearly || 0),
    0
  );
  const monthlySavings = totalMonthlyValue - formData.price_monthly;
  const savingsPercent =
    totalMonthlyValue > 0
      ? Math.round((monthlySavings / totalMonthlyValue) * 100)
      : 0;

  return (
    <div className="space-y-6" data-testid="admin-bundles">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-white uppercase">
            Bundles
          </h1>
          <p className="text-zinc-400 mt-1">
            Create feature bundles with discounted pricing
          </p>
        </div>
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={fetchData}
            className="border-zinc-700 text-white"
          >
            <ArrowsClockwise size={18} className="mr-2" />
            Refresh
          </Button>
          <Button
            onClick={() => setIsDialogOpen(true)}
            className="bg-[#FF6600] hover:bg-[#CC5200] text-white"
          >
            <Plus size={18} className="mr-2" />
            Add Bundle
          </Button>
          <AdminRecordModal
            open={isDialogOpen}
            onOpenChange={(open) => {
              setIsDialogOpen(open);
              if (!open) resetForm();
            }}
            title={`${editingBundle ? "Edit" : "Create"} Bundle`}
            size="md"
            footer={
              <AdminModalFooter onCancel={() => { setIsDialogOpen(false); resetForm(); }}>
                <Button
                  onClick={handleSubmit}
                  className="bg-[#FF6600] hover:bg-[#CC5200] text-white"
                >
                  {editingBundle ? "Update" : "Create"}
                </Button>
              </AdminModalFooter>
            }
          >
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium text-zinc-300">Name</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => {
                      const name = e.target.value;
                      setFormData({
                        ...formData,
                        name,
                        slug: editingBundle ? formData.slug : generateSlug(name),
                      });
                    }}
                    className="bg-zinc-800 border-zinc-700 text-white"
                    required
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium text-zinc-300">Slug</Label>
                  <Input
                    value={formData.slug}
                    onChange={(e) =>
                      setFormData({ ...formData, slug: e.target.value })
                    }
                    className="bg-zinc-800 border-zinc-700 text-white"
                    required
                    disabled={!!editingBundle}
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label className="text-sm font-medium text-zinc-300">Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  className="bg-zinc-800 border-zinc-700 text-white"
                  rows={2}
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium text-zinc-300">Monthly ($)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.price_monthly}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        price_monthly: parseFloat(e.target.value) || 0,
                      })
                    }
                    className="bg-zinc-800 border-zinc-700 text-white"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium text-zinc-300">Yearly ($)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.price_yearly}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        price_yearly: parseFloat(e.target.value) || 0,
                      })
                    }
                    className="bg-zinc-800 border-zinc-700 text-white"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium text-zinc-300">Discount %</Label>
                  <Input
                    type="number"
                    value={formData.discount_percent}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        discount_percent: parseFloat(e.target.value) || 0,
                      })
                    }
                    className="bg-zinc-800 border-zinc-700 text-white"
                  />
                </div>
              </div>

              {/* Savings Preview */}
              {selectedFeatures.length > 0 && (
                <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-emerald-400 text-sm">
                    <Tag size={16} />
                    <span>
                      Total feature value: ${totalMonthlyValue.toFixed(2)}/mo •
                      Bundle price: ${formData.price_monthly}/mo •{" "}
                      <strong>Save {savingsPercent}%</strong>
                    </span>
                  </div>
                </div>
              )}

              <div className="space-y-1.5">
                <Label className="text-sm font-medium text-zinc-300">Included Features</Label>
                <div className="mt-2 space-y-1 max-h-48 overflow-y-auto border border-zinc-700/50 rounded-lg p-2 bg-zinc-800/30">
                  {features
                    .filter((f) => f.is_active)
                    .map((feature) => (
                      <label
                        key={feature.feature_id}
                        className="flex items-center gap-3 p-2 rounded-lg cursor-pointer hover:bg-zinc-800"
                      >
                        <Checkbox
                          checked={formData.feature_ids.includes(feature.feature_id)}
                          onCheckedChange={() => toggleFeature(feature.feature_id)}
                        />
                        <span className="flex-1 text-white">{feature.name}</span>
                        <span className="text-xs text-zinc-500">
                          ${feature.price_monthly}/mo
                        </span>
                      </label>
                    ))}
                </div>
                <p className="text-xs text-zinc-500">
                  {selectedFeatures.length} features selected
                </p>
              </div>

              <div className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg border border-zinc-700/50">
                <div>
                  <Label className="text-sm font-medium text-zinc-300">Active</Label>
                  <p className="text-xs text-zinc-500">
                    Inactive bundles are hidden from purchase
                  </p>
                </div>
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={(checked) =>
                    setFormData({ ...formData, is_active: checked })
                  }
                />
              </div>
          </AdminRecordModal>
        </div>
      </div>

      {loading ? (
        <div className="space-y-4">
          {[...Array(2)].map((_, i) => (
            <div key={i} className="h-40 bg-zinc-800 rounded-sm animate-pulse" />
          ))}
        </div>
      ) : (
        <div className="space-y-4">
          {bundles.map((bundle) => {
            const bundleFeatures = bundle.features || [];
            const totalValue = bundleFeatures.reduce(
              (sum, f) => sum + (f.price_monthly || 0),
              0
            );
            const savings = totalValue - bundle.price_monthly;
            const savingsPct =
              totalValue > 0 ? Math.round((savings / totalValue) * 100) : 0;

            return (
              <Card
                key={bundle.bundle_id}
                className={`bg-zinc-900 border-zinc-800 ${
                  !bundle.is_active ? "opacity-60" : ""
                }`}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-start gap-4">
                      <div
                        className={`w-12 h-12 flex items-center justify-center rounded-sm ${
                          bundle.is_active
                            ? "bg-[#FF6600]/20 text-[#FF6600]"
                            : "bg-zinc-700 text-zinc-500"
                        }`}
                      >
                        <Package size={24} />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-white">
                          {bundle.name}
                        </h3>
                        <p className="text-sm text-zinc-500">{bundle.slug}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="text-2xl font-bold text-[#FF6600]">
                          ${bundle.price_monthly}
                          <span className="text-sm text-zinc-500">/mo</span>
                        </p>
                        <p className="text-sm text-zinc-500">
                          ${bundle.price_yearly}/yr
                        </p>
                        {savingsPct > 0 && (
                          <Badge className="bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 mt-1">
                            Save {savingsPct}%
                          </Badge>
                        )}
                      </div>
                      <div className="flex flex-col gap-2">
                        <button onClick={() => toggleActive(bundle)}>
                          {bundle.is_active ? (
                            <Badge className="bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 cursor-pointer hover:bg-emerald-500/30">
                              <CheckCircle size={12} className="mr-1" />
                              Active
                            </Badge>
                          ) : (
                            <Badge className="bg-zinc-700 text-zinc-400 border border-zinc-600 cursor-pointer hover:bg-zinc-600">
                              <XCircle size={12} className="mr-1" />
                              Inactive
                            </Badge>
                          )}
                        </button>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => editBundle(bundle)}
                            className="text-zinc-400 hover:text-white h-8 w-8"
                          >
                            <PencilSimple size={16} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => deleteBundle(bundle.bundle_id)}
                            className="text-red-400 hover:text-red-300 h-8 w-8"
                          >
                            <Trash size={16} />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <p className="text-zinc-400 mb-4">{bundle.description}</p>

                  <div className="pt-4 border-t border-zinc-800">
                    <p className="text-sm text-zinc-500 mb-2">
                      Included Features ({bundleFeatures.length}):
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {bundleFeatures.map((feature) => (
                        <Badge
                          key={feature.feature_id}
                          variant="outline"
                          className={`border-zinc-700 ${
                            feature.is_active ? "text-zinc-300" : "text-zinc-500"
                          }`}
                        >
                          <CheckCircle
                            size={14}
                            className={`mr-1 ${
                              feature.is_active
                                ? "text-emerald-400"
                                : "text-zinc-500"
                            }`}
                          />
                          {feature.name}
                          {!feature.is_active && " (inactive)"}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
